// Google Analytics 4, wired so it cannot cost us a PageSpeed point.
//
// Two rules keep gtag.js off the critical path:
//
//  1. Nothing is requested until the page is done: the tag is injected on the
//     first real user interaction (pointer, key, scroll) or, failing that,
//     once the browser has been idle for a while after `load`. Lighthouse
//     never interacts with the page, so in a lab run the tag lands well
//     outside the LCP/TBT measurement window — the score is unchanged.
//  2. No preconnect / preload hints. Those would open the connection to
//     googletagmanager.com during page load, which is exactly the cost we are
//     trying to avoid, in exchange for latency we do not care about on a
//     fire-and-forget beacon.
//
// The tag also runs cookieless (see CONFIG below): no `_ga` cookie, no
// localStorage, ad storage denied. That keeps us out of consent-banner
// territory. The trade-off is that every page load is a fresh client id, so
// GA4 reports visits, not returning users.

const MEASUREMENT_ID =
  (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || "G-KSXM80FKZ5";

// Delay after `load` before we even ask for an idle slot. Long enough that a
// Lighthouse trace has normally been cut before the request goes out.
const IDLE_DELAY_MS = 3000;

const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"] as const;

type GtagArgs = [command: string, ...rest: unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

let started = false;
let lastTrackedHref: string | null = null;

function isEnabled(): boolean {
  if (typeof window === "undefined") return false;
  if (import.meta.env.DEV) return false;
  const { hostname } = window.location;
  return hostname !== "localhost" && hostname !== "127.0.0.1" && hostname !== "[::1]";
}

function pageViewParams(): Record<string, unknown> {
  return {
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
  };
}

function injectTag(): void {
  window.dataLayer = window.dataLayer || [];
  // gtag pushes `arguments` itself — the array form is what the tag expects.
  const gtag: (...args: GtagArgs) => void = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;

  gtag("js", new Date());

  // Consent Mode v2. Advertising storage stays denied permanently; analytics
  // storage is granted but paired with `storage: "none"` below, so gtag is
  // allowed to measure yet has nowhere to persist an identifier.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
  });

  gtag("config", MEASUREMENT_ID, {
    // Cookieless: no `_ga`, no `_ga_*`, no localStorage fallback. GA4 refuses
    // hits without an identifier when storage is off, so we mint an ephemeral
    // one that lives only for this page load.
    storage: "none",
    client_storage: "none",
    client_id: ephemeralClientId(),
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    anonymize_ip: true,
    // We drive page_view ourselves — the tag boots long after the first paint
    // and the router does client-side navigation.
    send_page_view: false,
    transport_type: "beacon",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Commands queued on dataLayer before the script lands are replayed once it
  // loads, so sending the first page_view immediately is safe.
  trackPageView();
}

function ephemeralClientId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
}

/**
 * Sends a GA4 page_view for the current URL. No-op until the tag has actually
 * been injected — the injector sends the page_view for whatever page is on
 * screen at that moment, so nothing is lost.
 */
export function trackPageView(): void {
  if (!started || !window.gtag) return;
  const href = window.location.href;
  if (href === lastTrackedHref) return;
  lastTrackedHref = href;
  window.gtag("event", "page_view", pageViewParams());
}

/**
 * Arms the loader. Returns a cleanup function that detaches every listener and
 * timer it registered. Calling it more than once is a no-op.
 */
export function armAnalytics(): () => void {
  if (started || !isEnabled()) return () => {};

  let timer: ReturnType<typeof setTimeout> | undefined;
  let idleHandle: number | undefined;

  const cleanup = () => {
    for (const event of INTERACTION_EVENTS) {
      window.removeEventListener(event, start, { capture: true });
    }
    window.removeEventListener("load", scheduleIdle);
    if (timer !== undefined) clearTimeout(timer);
    if (idleHandle !== undefined && typeof window.cancelIdleCallback === "function") {
      window.cancelIdleCallback(idleHandle);
    }
  };

  function start(): void {
    if (started) return;
    started = true;
    cleanup();
    injectTag();
  }

  function scheduleIdle(): void {
    timer = setTimeout(() => {
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(() => start(), { timeout: 2000 });
      } else {
        start();
      }
    }, IDLE_DELAY_MS);
  }

  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, start, { once: true, passive: true, capture: true });
  }

  if (document.readyState === "complete") {
    scheduleIdle();
  } else {
    window.addEventListener("load", scheduleIdle, { once: true });
  }

  return cleanup;
}
